import React, { useState, useRef, useEffect, useCallback } from 'react';
import { useAuth } from '../../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import { patientsApi, fetchWithAuth } from '../../lib/api';
import { Bell, Search, Menu, Loader2, UserRound, Camera, Wand2, Video, UserPlus, Building2, AlertTriangle, ArrowUpRight, ArrowDownRight, RotateCcw } from 'lucide-react';
import { Popover, PopoverContent, PopoverTrigger } from '../ui/popover';

const NOTIF_META = {
  scan_generated:           { icon: Camera,        text: '4K üretimi tamamlandı' },
  ultrasound_4k_started:    { icon: Camera,        text: '4K üretimi başladı' },
  ultrasound_4k_completed:  { icon: Camera,        text: '4K üretimi tamamlandı' },
  ai_face_generated:        { icon: Wand2,         text: 'AI bebek yüzü üretildi' },
  video_generation_started: { icon: Video,         text: '4D video başlatıldı' },
  user_created:             { icon: UserPlus,      text: 'Yeni doktor kayıt' },
  mom_registered:           { icon: UserPlus,      text: 'Yeni anne kayıt' },
  clinic_created:           { icon: Building2,     text: 'Yeni klinik' },
  credit_granted:           { icon: ArrowUpRight,  text: 'Kredi verildi' },
  credit_spent:             { icon: ArrowDownRight,text: 'Kredi kullanıldı' },
  credit_refunded:          { icon: RotateCcw,     text: 'Kredi iade' },
  scan_failed:              { icon: AlertTriangle, text: 'Başarısız üretim' },
};

function timeAgoTr(iso) {
  if (!iso) return '';
  const sec = Math.max(0, Math.floor((Date.now() - new Date(iso).getTime()) / 1000));
  if (sec < 5) return 'şimdi';
  if (sec < 60) return `${sec} sn önce`;
  const min = Math.floor(sec / 60);
  if (min < 60) return `${min} dk önce`;
  const hr = Math.floor(min / 60);
  if (hr < 24) return `${hr} sa önce`;
  return new Date(iso).toLocaleDateString('tr-TR');
}

export const Topbar = ({ title, subtitle, onMenuClick }) => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const isSuperAdmin = user?.role === 'super_admin';

  // Search state
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [isSearching, setIsSearching] = useState(false);
  const [showDropdown, setShowDropdown] = useState(false);
  const searchRef = useRef(null);
  const debounceRef = useRef(null);

  // Notifications — real backend data for super_admin only
  const [notifications, setNotifications] = useState([]);
  const [notifLoading, setNotifLoading] = useState(false);
  const [readIds, setReadIds] = useState(() => new Set(JSON.parse(localStorage.getItem('notif_read') || '[]')));

  const unreadCount = notifications.filter((n) => !readIds.has(n.id)).length;

  const fetchNotifications = useCallback(async () => {
    if (!isSuperAdmin) return;
    setNotifLoading(true);
    try {
      const data = await fetchWithAuth('/superadmin/activity-feed/notifications?limit=8');
      if (Array.isArray(data)) setNotifications(data);
    } catch {
      // silent — bell shows empty state
    } finally {
      setNotifLoading(false);
    }
  }, [isSuperAdmin]);

  useEffect(() => {
    if (!isSuperAdmin) return;
    fetchNotifications();
    const id = setInterval(fetchNotifications, 15000);
    return () => clearInterval(id);
  }, [isSuperAdmin, fetchNotifications]);

  const markAllRead = () => {
    const allIds = new Set(notifications.map((n) => n.id));
    setReadIds(allIds);
    localStorage.setItem('notif_read', JSON.stringify([...allIds]));
  };

  // Patient search with debounce
  const handleSearch = useCallback(async (query) => {
    if (!query || query.length < 2) { setSearchResults([]); setShowDropdown(false); return; }
    setIsSearching(true);
    try {
      const result = await patientsApi.list({ search: query, limit: 5 });
      const patients = result.patients || result || [];
      setSearchResults(patients);
      setShowDropdown(patients.length > 0);
    } catch { setSearchResults([]); }
    finally { setIsSearching(false); }
  }, []);

  const onSearchChange = (e) => {
    const val = e.target.value;
    setSearchQuery(val);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => handleSearch(val), 300);
  };

  useEffect(() => {
    const handler = (e) => {
      if (searchRef.current && !searchRef.current.contains(e.target)) setShowDropdown(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  return (
    <header className="h-16 bg-white border-b border-gray-200 sticky top-0 z-30 flex items-center justify-between px-4 md:px-8" data-testid="topbar">
      <div className="flex items-center gap-3">
        {onMenuClick && (
          <button onClick={onMenuClick} className="p-2 rounded-lg hover:bg-gray-100 transition-colors md:hidden" data-testid="topbar-menu-button">
            <Menu className="w-5 h-5 text-gray-600" strokeWidth={1.5} />
          </button>
        )}
        <div>
          <h1 className="text-lg font-medium text-gray-900" style={{ fontFamily: 'Manrope, sans-serif' }}>{title}</h1>
          {subtitle && <p className="text-xs text-gray-500" style={{ fontFamily: "'IBM Plex Sans', sans-serif" }}>{subtitle}</p>}
        </div>
      </div>

      <div className="flex items-center gap-2">
        {/* Search */}
        <div className="hidden md:block relative" ref={searchRef}>
          <div className="flex items-center gap-2 px-3 py-2 bg-gray-50 rounded-lg border border-gray-200 focus-within:border-[#5A67D8]/40 focus-within:bg-white transition-all">
            {isSearching ? <Loader2 className="w-4 h-4 text-gray-400 animate-spin" /> : <Search className="w-4 h-4 text-gray-400" strokeWidth={1.5} />}
            <input
              type="text" placeholder="Hasta ara..." value={searchQuery} onChange={onSearchChange}
              onFocus={() => searchResults.length > 0 && setShowDropdown(true)}
              className="bg-transparent border-none outline-none text-sm text-gray-700 placeholder-gray-400 w-40 lg:w-52"
              style={{ fontFamily: "'IBM Plex Sans', sans-serif" }}
              data-testid="topbar-search"
            />
          </div>
          {showDropdown && (
            <div className="absolute top-full mt-1 left-0 right-0 bg-white rounded-lg shadow-lg border border-gray-200 overflow-hidden z-50" data-testid="search-results-dropdown">
              {searchResults.map((p) => (
                <button key={p.id} onClick={() => { navigate(`/doctor/patients/${p.id}`); setShowDropdown(false); setSearchQuery(''); }}
                  className="w-full flex items-center gap-3 px-4 py-3 hover:bg-gray-50 transition-colors text-left" data-testid={`search-result-${p.id}`}>
                  <div className="w-8 h-8 rounded-full bg-[#5A67D8]/10 flex items-center justify-center flex-shrink-0">
                    <UserRound className="w-4 h-4 text-[#5A67D8]" strokeWidth={1.5} />
                  </div>
                  <div className="min-w-0">
                    <p className="text-sm font-medium text-gray-800 truncate">{p.first_name} {p.last_name}</p>
                    <p className="text-xs text-gray-400">{p.pregnancy_week ? `${p.pregnancy_week}. hafta` : ''}</p>
                  </div>
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Notification Bell - Functional */}
        <Popover>
          <PopoverTrigger asChild>
            <button className="relative p-2 rounded-lg hover:bg-gray-100 transition-colors" data-testid="notification-bell">
              <Bell className="w-5 h-5 text-gray-600" strokeWidth={1.5} />
              {unreadCount > 0 && (
                <span className="absolute top-1.5 right-1.5 w-2 h-2 bg-red-500 rounded-full ring-2 ring-white" />
              )}
            </button>
          </PopoverTrigger>
          <PopoverContent align="end" className="w-80 p-0" data-testid="notification-panel">
            <div className="flex items-center justify-between px-4 py-3 border-b border-gray-100">
              <h3 className="text-sm font-semibold text-gray-900" style={{ fontFamily: 'Manrope, sans-serif' }}>Bildirimler</h3>
              {isSuperAdmin && unreadCount > 0 && (
                <button onClick={markAllRead} className="text-xs text-[#5A67D8] hover:underline" data-testid="mark-all-read">
                  Tümünü Oku
                </button>
              )}
            </div>
            <div className="max-h-72 overflow-y-auto">
              {!isSuperAdmin ? (
                <div className="px-4 py-8 text-center text-sm text-gray-400">Şu an bildirim yok</div>
              ) : notifLoading && notifications.length === 0 ? (
                <div className="px-4 py-8 text-center text-sm text-gray-400">Yükleniyor...</div>
              ) : notifications.length === 0 ? (
                <div className="px-4 py-8 text-center text-sm text-gray-400" data-testid="notification-empty">Henüz bildirim yok</div>
              ) : (
                notifications.map((n) => {
                  const meta = NOTIF_META[n.activity_type] || { icon: Bell, text: n.activity_type };
                  const Icon = meta.icon;
                  const isUnread = !readIds.has(n.id);
                  return (
                    <div key={n.id} className={`flex items-start gap-3 px-4 py-3 border-b border-gray-50 last:border-0 transition-colors ${isUnread ? 'bg-[#5A67D8]/5' : 'hover:bg-gray-50'}`} data-testid={`notif-${n.activity_type}`}>
                      <div className="w-8 h-8 rounded-lg bg-slate-100 flex items-center justify-center flex-shrink-0">
                        <Icon className="w-4 h-4 text-slate-600" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className={`text-sm leading-tight ${isUnread ? 'font-medium text-gray-900' : 'text-gray-600'}`}>{meta.text} <span className="text-gray-400">· {n.actor_name}</span></p>
                        <p className="text-xs text-gray-400 mt-0.5">{timeAgoTr(n.created_at)}</p>
                      </div>
                      {isUnread && <span className="w-2 h-2 rounded-full bg-[#5A67D8] mt-2" />}
                    </div>
                  );
                })
              )}
            </div>
            {isSuperAdmin && notifications.length > 0 && (
              <div className="px-4 py-2 border-t border-gray-100 bg-gray-50">
                <button
                  onClick={() => navigate('/superadmin/audit-log')}
                  className="w-full text-center text-xs text-[#5A67D8] hover:underline font-medium"
                  data-testid="view-all-notifications"
                >
                  Tümünü Gör
                </button>
              </div>
            )}
          </PopoverContent>
        </Popover>
      </div>
    </header>
  );
};
